import type { ColumnDef } from '@tanstack/react-table';
import { columnVisibilityFeature, rowSortingFeature, tableFeatures } from '@tanstack/react-table';
import type { CandidateListItemDto } from '@sara/api-client';
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { formatDate } from '../../../../utils/formatDate';
import { InitialsAvatar } from '../InitialsAvatar';

/**
 * The table-v9 feature registry for this table. Declared at module scope (the
 * table only reads its type, so a per-render object would be wasted work).
 * Sorting here is fully manual/server-side (no `sortedRowModel`), but
 * `rowSortingFeature` is still required for `state.sorting`,
 * `onSortingChange`, and `column.getIsSorted()`/`toggleSorting()`.
 */
export const features = tableFeatures({
  rowSortingFeature,
  // `row.getVisibleCells()`
  columnVisibilityFeature,
});

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc') return <ArrowUp className="ml-1 inline h-3.5 w-3.5" strokeWidth={1.6} />;
  if (sorted === 'desc') return <ArrowDown className="ml-1 inline h-3.5 w-3.5" strokeWidth={1.6} />;
  return <ChevronsUpDown className="ml-1 inline h-3 w-3 opacity-50" strokeWidth={1.6} />;
}

function SortHeader({
  children,
  sorted,
  onClick,
}: {
  children: React.ReactNode;
  sorted: false | 'asc' | 'desc';
  onClick: () => void;
}) {
  return (
    <button
      className="inline-flex h-full items-center text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground/80 transition-colors hover:text-foreground"
      onClick={onClick}
    >
      {children}
      <SortIcon sorted={sorted} />
    </button>
  );
}

export const candidateColumns: ColumnDef<typeof features, CandidateListItemDto>[] = [
  {
    id: 'name',
    enableSorting: true,
    header: ({ column }) => (
      <SortHeader sorted={column.getIsSorted()} onClick={() => column.toggleSorting()}>
        Candidate
      </SortHeader>
    ),
    cell: ({ row }) => {
      const { firstName, lastName, email } = row.original;
      return (
        <div className="flex items-center gap-3">
          <InitialsAvatar first={firstName} last={lastName} />
          <div className="min-w-0">
            <p className="truncate text-[14px] font-medium tracking-tight text-foreground">
              {firstName} {lastName}
            </p>
            <p className="truncate text-[12.5px] text-muted-foreground">{email}</p>
          </div>
        </div>
      );
    },
  },
  {
    id: 'interviewCount',
    enableSorting: false,
    header: 'Interviews',
    cell: ({ row }) => (
      <span className="text-[13.5px] tabular-nums text-foreground">
        {row.original.interviewCount}
      </span>
    ),
  },
  {
    id: 'lastInterviewAt',
    enableSorting: false,
    header: 'Last interview',
    cell: ({ row }) => (
      <span className="text-[13px] tabular-nums text-muted-foreground">
        {formatDate(row.original.lastInterviewAt)}
      </span>
    ),
  },
  {
    id: 'createdAt',
    enableSorting: true,
    header: ({ column }) => (
      <SortHeader sorted={column.getIsSorted()} onClick={() => column.toggleSorting()}>
        Created
      </SortHeader>
    ),
    cell: ({ row }) => (
      <span className="text-[13px] tabular-nums text-muted-foreground">
        {formatDate(row.original.createdAt)}
      </span>
    ),
  },
];
